/**
 * Invoice Service - Document Generation Domain
 *
 * AI-powered invoice generation and payment lifecycle management.
 * Follows Phase 4 architecture with ServiceRegistry integration.
 *
 * Key Flows:
 * 1. Auto-generate from accepted proposal (mycelium: proposal.accepted → invoice.created)
 * 2. Manual creation with line items
 * 3. Payment recording with auto-status updates (trigger)
 * 4. Xero sync events via mycelium backbone
 *
 * @module services/document-generation/invoice.service
 * @since 2026-02-20
 */
import { supabase } from '../../integrations/supabase/client';
import { createServiceError, type ServiceResponse, type IService, } from '../service-registry';
import { logger } from '../../lib/logger';
import type { Invoice, InvoiceStatus, InvoicePayment, GenerateInvoiceRequest, UpdateInvoiceRequest, DocumentGenerationFilters, InvoiceAnalytics, DocumentTemplate, } from '@flowstate/types';
// ============================================================================
// SERVICE INTERFACE
// ============================================================================
export interface IInvoiceService extends IService {
    generateInvoice(userId: string, request: GenerateInvoiceRequest): Promise<ServiceResponse<Invoice>>;
    generateFromProposal(proposalId: string, userId: string): Promise<ServiceResponse<Invoice>>;
    getInvoice(invoiceId: string): Promise<ServiceResponse<Invoice>>;
    listInvoices(userId: string, filters?: DocumentGenerationFilters): Promise<ServiceResponse<Invoice[]>>;
    updateInvoice(invoiceId: string, request: UpdateInvoiceRequest): Promise<ServiceResponse<Invoice>>;
    updateInvoiceStatus(invoiceId: string, status: InvoiceStatus): Promise<ServiceResponse<Invoice>>;
    recordPayment(invoiceId: string, payment: Omit<InvoicePayment, 'id' | 'invoice_id' | 'created_at'>): Promise<ServiceResponse<InvoicePayment>>;
    deleteInvoice(invoiceId: string): Promise<ServiceResponse<void>>;
    getInvoiceAnalytics(teamId?: string): Promise<ServiceResponse<InvoiceAnalytics>>;
    listTemplates(templateType: 'invoice'): Promise<ServiceResponse<DocumentTemplate[]>>;
    getOverdueInvoices(): Promise<ServiceResponse<Invoice[]>>;
}
// ============================================================================
// SERVICE IMPLEMENTATION
// ============================================================================
export class InvoiceService implements IInvoiceService {
    readonly name = 'invoice';
    async initialize(): Promise<void> {
        logger.info('🧾 Invoice service initialized');
    }
    async dispose(): Promise<void> {
        logger.info('🧾 Invoice service disposed');
    }
    // --------------------------------------------------------------------------
    // GENERATE INVOICE
    // --------------------------------------------------------------------------
    async generateInvoice(userId: string, request: GenerateInvoiceRequest): Promise<ServiceResponse<Invoice>> {
        try {
            logger.info('🧾 Generating invoice', { userId, fromProposal: !!request.proposal_id });
            // Generate invoice number
            const { data: numberData, error: numberError } = await supabase
                .rpc('generate_invoice_number');
            if (numberError) {
                return { success: false, error: numberError.message, code: 'INVOICE_NUMBER_FAILED', service: this.name };
            }
            const invoiceNumber = numberData as string;
            // Build line items
            const lineItems = (request.line_items || []).map((item, index) => {
                const discount = item.discount_percent ? item.quantity * item.unit_price * (item.discount_percent / 100) : 0;
                const subtotal = item.quantity * item.unit_price - discount;
                const tax = item.tax_rate ? subtotal * (item.tax_rate / 100) : 0;
                return {
                    id: crypto.randomUUID(),
                    description: item.description,
                    quantity: item.quantity,
                    unit_price: item.unit_price,
                    discount_percent: item.discount_percent || 0,
                    tax_rate: item.tax_rate || 0,
                    tax_type: item.tax_type,
                    total: subtotal + tax,
                    sort_order: index,
                    account_code: item.account_code,
                };
            });
            // Calculate totals
            const subtotal = lineItems.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
            const discountAmount = lineItems.reduce((sum, item) => {
                return sum + (item.discount_percent ? item.quantity * item.unit_price * (item.discount_percent / 100) : 0);
            }, 0);
            const taxAmount = lineItems.reduce((sum, item) => {
                const itemSubtotal = item.quantity * item.unit_price - (item.discount_percent ? item.quantity * item.unit_price * (item.discount_percent / 100) : 0);
                return sum + (item.tax_rate ? itemSubtotal * (item.tax_rate / 100) : 0);
            }, 0);
            const totalAmount = subtotal - discountAmount + taxAmount;
            // Calculate due date
            const dueDays = request.due_days || 14;
            const dueDate = new Date();
            dueDate.setDate(dueDate.getDate() + dueDays);

            let organizationId = request.organization_id || null;
            if (!organizationId && request.client_id) {
                const { data: client } = await supabase
                    .from('clients')
                    .select('organization_id')
                    .eq('id', request.client_id)
                    .maybeSingle();
                organizationId = client?.organization_id ?? null;
            }
            if (!organizationId && request.opportunity_id) {
                const { data: opportunity } = await supabase
                    .from('opportunities')
                    .select('organization_id')
                    .eq('id', request.opportunity_id)
                    .maybeSingle();
                organizationId = opportunity?.organization_id ?? null;
            }

            const invoiceData = {
                invoice_number: invoiceNumber,
                status: 'draft' as InvoiceStatus,
                organization_id: organizationId,
                invoice_type: request.invoice_type || 'ACCREC',
                sync_status: 'local_only' as const,
                client_id: request.client_id || null,
                opportunity_id: request.opportunity_id || null,
                proposal_id: request.proposal_id || null,
                created_by: userId,
                team_id: request.team_id || null,
                line_items: JSON.stringify(lineItems),
                subtotal,
                tax_amount: taxAmount,
                discount_amount: discountAmount,
                total_amount: totalAmount,
                amount_paid: 0,
                amount_due: totalAmount,
                currency: request.currency || 'AUD',
                issue_date: new Date().toISOString(),
                due_date: dueDate.toISOString(),
                payment_terms: request.payment_terms || `Net ${dueDays}`,
                ai_generated: false,
                source_proposal_id: request.proposal_id || null,
                notes: request.notes || null,
            };
            const { data, error } = await supabase
                .from('invoices')
                .insert(invoiceData)
                .select()
                .single();
            if (error) {
                return { success: false, error: error.message, code: 'INVOICE_CREATE_FAILED', service: this.name };
            }
            const invoice = this.mapInvoice(data);
            logger.info('✅ Invoice created', { invoiceId: invoice.id, number: invoice.invoice_number });
            return { success: true, data: invoice };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'INVOICE_GENERATE_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // GENERATE FROM PROPOSAL (mycelium flow)
    // --------------------------------------------------------------------------
    async generateFromProposal(proposalId: string, userId: string): Promise<ServiceResponse<Invoice>> {
        try {
            logger.info('🧾 Generating invoice from proposal', { proposalId });
            // Fetch the accepted proposal
            const { data: proposal, error: proposalError } = await supabase
                .from('proposals')
                .select('*')
                .eq('id', proposalId)
                .single();
            if (proposalError || !proposal) {
                return { success: false, error: 'Source proposal not found', code: 'PROPOSAL_NOT_FOUND', service: this.name };
            }
            // Parse proposal line items
            const proposalLineItems = typeof proposal.line_items === 'string'
                ? JSON.parse(proposal.line_items)
                : (proposal.line_items || []);
            const lineItems = proposalLineItems.map((item: Record<string, unknown>, index: number) => ({
                description: item.description as string,
                quantity: Number(item.quantity),
                unit_price: Number(item.unit_price),
                discount_percent: Number(item.discount_percent) || 0,
                tax_rate: Number(item.tax_rate) || 0,
                account_code: item.account_code as string | undefined,
            }));
            return this.generateInvoice(userId, {
                organization_id: proposal.organization_id ?? undefined,
                client_id: proposal.client_id,
                opportunity_id: proposal.opportunity_id,
                proposal_id: proposalId,
                team_id: proposal.team_id,
                line_items: lineItems,
                currency: proposal.currency || 'AUD',
                notes: `Generated from proposal ${proposal.proposal_number}`,
            });
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'INVOICE_FROM_PROPOSAL_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // GET INVOICE
    // --------------------------------------------------------------------------
    async getInvoice(invoiceId: string): Promise<ServiceResponse<Invoice>> {
        try {
            const { data, error } = await supabase
                .from('invoices')
                .select('*')
                .eq('id', invoiceId)
                .single();
            if (error || !data) {
                return { success: false, error: error?.message || 'Invoice not found', code: 'INVOICE_NOT_FOUND', service: this.name };
            }
            // Fetch payments
            const { data: payments } = await supabase
                .from('invoice_payments')
                .select('*')
                .eq('invoice_id', invoiceId)
                .order('payment_date', { ascending: false });
            const invoice = this.mapInvoice(data);
            invoice.payments = (payments || []).map(p => this.mapPayment(p));
            return { success: true, data: invoice };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'INVOICE_FETCH_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // LIST INVOICES
    // --------------------------------------------------------------------------
    async listInvoices(userId: string, filters?: DocumentGenerationFilters): Promise<ServiceResponse<Invoice[]>> {
        try {
            let query = supabase
                .from('invoices')
                .select('*')
                .order('created_at', { ascending: false });
            if (filters?.organization_id)
                query = query.eq('organization_id', filters.organization_id);
            if (filters?.client_id)
                query = query.eq('client_id', filters.client_id);
            if (filters?.opportunity_id)
                query = query.eq('opportunity_id', filters.opportunity_id);
            if (filters?.team_id)
                query = query.eq('team_id', filters.team_id);
            if (filters?.status?.length)
                query = query.in('status', filters.status);
            if (filters?.sync_status?.length)
                query = query.in('sync_status', filters.sync_status);
            if (filters?.date_from)
                query = query.gte('created_at', filters.date_from);
            if (filters?.date_to)
                query = query.lte('created_at', filters.date_to);
            if (filters?.amount_min)
                query = query.gte('total_amount', filters.amount_min);
            if (filters?.amount_max)
                query = query.lte('total_amount', filters.amount_max);
            if (filters?.ai_generated !== undefined)
                query = query.eq('ai_generated', filters.ai_generated);
            if (filters?.search)
                query = query.ilike('invoice_number', `%${filters.search}%`);
            const { data, error } = await query;
            if (error) {
                return { success: false, error: error.message, code: 'INVOICES_FETCH_FAILED', service: this.name };
            }
            const invoices = (data || []).map(row => this.mapInvoice(row));
            return { success: true, data: invoices };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'INVOICES_FETCH_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // UPDATE INVOICE
    // --------------------------------------------------------------------------
    async updateInvoice(invoiceId: string, request: UpdateInvoiceRequest): Promise<ServiceResponse<Invoice>> {
        try {
            const updateData: Record<string, unknown> = {};
            if (request.status !== undefined)
                updateData.status = request.status;
            if (request.due_date !== undefined)
                updateData.due_date = request.due_date;
            if (request.payment_terms !== undefined)
                updateData.payment_terms = request.payment_terms;
            if (request.notes !== undefined)
                updateData.notes = request.notes;
            if (request.metadata !== undefined)
                updateData.metadata = JSON.stringify(request.metadata);
            if (request.line_items !== undefined) {
                updateData.line_items = JSON.stringify(request.line_items);
                const subtotal = request.line_items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
                const discountAmount = request.line_items.reduce((sum, item) => {
                    return sum + (item.discount_percent ? item.quantity * item.unit_price * (item.discount_percent / 100) : 0);
                }, 0);
                const taxAmount = request.line_items.reduce((sum, item) => {
                    const itemSubtotal = item.quantity * item.unit_price - (item.discount_percent ? item.quantity * item.unit_price * (item.discount_percent / 100) : 0);
                    return sum + (item.tax_rate ? itemSubtotal * (item.tax_rate / 100) : 0);
                }, 0);
                const totalAmount = subtotal - discountAmount + taxAmount;
                updateData.subtotal = subtotal;
                updateData.discount_amount = discountAmount;
                updateData.tax_amount = taxAmount;
                updateData.total_amount = totalAmount;
                updateData.amount_due = totalAmount; // Will be recalculated if payments exist
            }
            const { data, error } = await supabase
                .from('invoices')
                .update(updateData)
                .eq('id', invoiceId)
                .select()
                .single();
            if (error) {
                return { success: false, error: error.message, code: 'INVOICE_UPDATE_FAILED', service: this.name };
            }
            return { success: true, data: this.mapInvoice(data) };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'INVOICE_UPDATE_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // UPDATE INVOICE STATUS
    // --------------------------------------------------------------------------
    async updateInvoiceStatus(invoiceId: string, status: InvoiceStatus): Promise<ServiceResponse<Invoice>> {
        try {
            const updateData: Record<string, unknown> = { status };
            switch (status) {
                case 'sent':
                    updateData.sent_at = new Date().toISOString();
                    break;
                case 'viewed':
                    updateData.viewed_at = new Date().toISOString();
                    break;
                case 'paid':
                    updateData.paid_date = new Date().toISOString();
                    break;
            }
            const { data, error } = await supabase
                .from('invoices')
                .update(updateData)
                .eq('id', invoiceId)
                .select()
                .single();
            if (error) {
                return { success: false, error: error.message, code: 'INVOICE_STATUS_UPDATE_FAILED', service: this.name };
            }
            logger.info('🧾 Invoice status updated', { invoiceId, status });
            return { success: true, data: this.mapInvoice(data) };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'INVOICE_STATUS_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // RECORD PAYMENT
    // --------------------------------------------------------------------------
    async recordPayment(invoiceId: string, payment: Omit<InvoicePayment, 'id' | 'invoice_id' | 'created_at'>): Promise<ServiceResponse<InvoicePayment>> {
        try {
            const { data, error } = await supabase
                .from('invoice_payments')
                .insert({
                invoice_id: invoiceId,
                organization_id: payment.organization_id || null,
                amount: payment.amount,
                payment_method: payment.payment_method,
                payment_date: payment.payment_date,
                reference: payment.reference || null,
                notes: payment.notes || null,
                ledger_account_id: payment.ledger_account_id || null,
                xero_payment_id: payment.xero_payment_id || null,
            })
                .select()
                .single();
            if (error) {
                return { success: false, error: error.message, code: 'PAYMENT_RECORD_FAILED', service: this.name };
            }
            logger.info('💰 Payment recorded', { invoiceId, amount: payment.amount });
            return { success: true, data: this.mapPayment(data) };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'PAYMENT_RECORD_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // DELETE INVOICE
    // --------------------------------------------------------------------------
    async deleteInvoice(invoiceId: string): Promise<ServiceResponse<void>> {
        try {
            const { error } = await supabase
                .from('invoices')
                .delete()
                .eq('id', invoiceId);
            if (error) {
                return { success: false, error: error.message, code: 'INVOICE_DELETE_FAILED', service: this.name };
            }
            return { success: true, data: undefined as unknown as void };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'INVOICE_DELETE_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // ANALYTICS
    // --------------------------------------------------------------------------
    async getInvoiceAnalytics(teamId?: string): Promise<ServiceResponse<InvoiceAnalytics>> {
        try {
            let query = supabase.from('invoices').select('*');
            if (teamId)
                query = query.eq('team_id', teamId);
            const { data, error } = await query;
            if (error) {
                return { success: false, error: error.message, code: 'INVOICE_ANALYTICS_FAILED', service: this.name };
            }
            const invoices = data || [];
            const statusCounts = {} as Record<InvoiceStatus, number>;
            const statuses: InvoiceStatus[] = ['draft', 'ai_generating', 'review', 'sent', 'viewed', 'partial_payment', 'paid', 'overdue', 'cancelled', 'refunded'];
            statuses.forEach(s => { statusCounts[s] = invoices.filter(i => i.status === s).length; });
            const analytics: InvoiceAnalytics = {
                total_invoices: invoices.length,
                invoices_by_status: statusCounts,
                total_invoiced: invoices.reduce((s, i) => s + (i.total_amount || 0), 0),
                total_collected: invoices.reduce((s, i) => s + (i.amount_paid || 0), 0),
                total_outstanding: invoices.filter(i => !['paid', 'cancelled', 'refunded'].includes(i.status)).reduce((s, i) => s + (i.amount_due || 0), 0),
                total_overdue: invoices.filter(i => i.status === 'overdue').reduce((s, i) => s + (i.amount_due || 0), 0),
                average_days_to_payment: 0,
                collection_rate: 0,
                overdue_rate: invoices.length > 0 ? (invoices.filter(i => i.status === 'overdue').length / invoices.length) * 100 : 0,
            };
            const totalInvoiced = analytics.total_invoiced;
            if (totalInvoiced > 0) {
                analytics.collection_rate = (analytics.total_collected / totalInvoiced) * 100;
            }
            return { success: true, data: analytics };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'INVOICE_ANALYTICS_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // TEMPLATES
    // --------------------------------------------------------------------------
    async listTemplates(templateType: 'invoice'): Promise<ServiceResponse<DocumentTemplate[]>> {
        try {
            const { data, error } = await supabase
                .from('document_templates')
                .select('*')
                .eq('template_type', templateType)
                .eq('is_active', true)
                .order('name');
            if (error) {
                return { success: false, error: error.message, code: 'TEMPLATES_FETCH_FAILED', service: this.name };
            }
            return { success: true, data: (data || []) as DocumentTemplate[] };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'TEMPLATES_FETCH_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // OVERDUE INVOICES
    // --------------------------------------------------------------------------
    async getOverdueInvoices(): Promise<ServiceResponse<Invoice[]>> {
        try {
            const { data, error } = await supabase
                .from('invoices')
                .select('*')
                .in('status', ['sent', 'viewed', 'partial_payment'])
                .lt('due_date', new Date().toISOString())
                .order('due_date', { ascending: true });
            if (error) {
                return { success: false, error: error.message, code: 'OVERDUE_FETCH_FAILED', service: this.name };
            }
            const invoices = (data || []).map(row => this.mapInvoice(row));
            return { success: true, data: invoices };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'OVERDUE_FETCH_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // HELPERS
    // --------------------------------------------------------------------------
    private mapInvoice(row: Record<string, unknown>): Invoice {
        return {
            id: row.id as string,
            invoice_number: row.invoice_number as string,
            status: row.status as InvoiceStatus,
            organization_id: row.organization_id as string | null | undefined,
            invoice_type: (row.invoice_type as Invoice['invoice_type']) || 'ACCREC',
            sync_status: (row.sync_status as Invoice['sync_status']) || 'local_only',
            client_id: row.client_id as string | undefined,
            opportunity_id: row.opportunity_id as string | undefined,
            proposal_id: row.proposal_id as string | undefined,
            created_by: row.created_by as string,
            team_id: row.team_id as string | undefined,
            line_items: typeof row.line_items === 'string' ? JSON.parse(row.line_items) : (row.line_items || []),
            subtotal: Number(row.subtotal) || 0,
            tax_amount: Number(row.tax_amount) || 0,
            discount_amount: Number(row.discount_amount) || 0,
            total_amount: Number(row.total_amount) || 0,
            amount_paid: Number(row.amount_paid) || 0,
            amount_due: Number(row.amount_due) || 0,
            currency: (row.currency as string) || 'AUD',
            issue_date: row.issue_date as string,
            due_date: row.due_date as string,
            paid_date: row.paid_date as string | undefined,
            payment_terms: (row.payment_terms as string) || 'Net 14',
            payments: [],
            ai_generated: row.ai_generated as boolean,
            ai_model_used: row.ai_model_used as string | undefined,
            xero_invoice_id: row.xero_invoice_id as string | undefined,
            xero_synced_at: row.xero_synced_at as string | undefined,
            sent_at: row.sent_at as string | undefined,
            viewed_at: row.viewed_at as string | undefined,
            reminder_count: Number(row.reminder_count) || 0,
            last_reminder_at: row.last_reminder_at as string | undefined,
            source_proposal_id: row.source_proposal_id as string | undefined,
            notes: row.notes as string | undefined,
            metadata: row.metadata as Invoice['metadata'],
            created_at: row.created_at as string,
            updated_at: row.updated_at as string,
        };
    }
    private mapPayment(row: Record<string, unknown>): InvoicePayment {
        return {
            id: row.id as string,
            invoice_id: row.invoice_id as string,
            organization_id: row.organization_id as string | null | undefined,
            amount: Number(row.amount) || 0,
            payment_method: (row.payment_method as InvoicePayment['payment_method']) || 'bank_transfer',
            payment_date: row.payment_date as string,
            reference: row.reference as string | undefined,
            notes: row.notes as string | undefined,
            ledger_account_id: row.ledger_account_id as string | null | undefined,
            xero_payment_id: row.xero_payment_id as string | null | undefined,
            created_at: row.created_at as string,
        };
    }
}
/**
 * Singleton invoice service instance
 */
export const invoiceService = new InvoiceService();
